/**
 * sirus/Bags — R232q22 Phase 3 (Sirus 1:1 UI rebuild).
 *
 * The WoW 3.3.5a / Sirus inventory system. Replaces the old single-window
 * `Inventory.tsx` paper-doll with the proper "backpack + 4 additional bags"
 * model from Wrath: each bag opens its own SirusFrame window stacked at the
 * bottom-right of the screen, cascaded 64 px above the previous one.
 *
 * Bag layout (per window):
 *   – 4 × 4 grid of `.aiwari-icon-slot` (16 slots each).
 *   – Slot shows: item icon, quality border colour, mono stack count.
 *   – Hover → tooltip (name / quality / stats / level / sell value).
 *
 * Keys (WotLK 3.3.5a parity):
 *   – B / И       toggles bag #0 (the main backpack).
 *   – F1..F4      toggles additional bags 1..4.
 *   – Shift+B / Shift+И  toggles ALL bags simultaneously.
 *
 * The keys are gated by `useUI().stage` and ignored while typing into
 * `<input>` / `<textarea>`. Modifier keys other than Shift+B fall through
 * so global shortcuts (Ctrl+B settings panel etc.) keep working.
 *
 * State sources:
 *   useEquip   — `equipped` map (Slot → itemId). Inventory itself isn't on
 *                a store yet; we keep `bagItems` local to the component.
 *                That's the same pattern the old Inventory.tsx used. The
 *                eventual server-bound items map will plug in here.
 *   useMatBag  — `bag` map (matId → qty). Rendered as the purple "reagent"
 *                bag (slot index 4) in the bottom-right bag bar.
 *
 * Drag & drop:
 *   – mime: `application/aiwari-item`, payload: `{bagIdx,slotIdx,itemId}`.
 *   – swaps within and across bags. Drag from a slot → drop on another
 *     slot moves the item; if the target is filled, the items swap.
 *
 * Right-click → small popover («Использовать», «Разделить стак», «Перетащить
 * в чат», «Уничтожить»). Each option is a stub that `console.log`s.
 *
 * Shift+click → emits a `aiwari-chat-link` window CustomEvent so the chat
 * input can splice in `[Имя предмета]`-style item links.
 *
 * Mobile (`@media (pointer: coarse)`): each bag window becomes a full-screen
 * sheet that slides up from the bottom; only one bag may be open at a time
 * (opening another auto-closes the previous). The bag bar at bottom-right
 * shrinks to 24px squares.
 *
 * DEFERRED (intentional, sibling agents own these):
 *   – Real item registry binding (we use placeholder data + ITEMS lookup).
 *   – Full tooltip stat block parity with WotLK 3.3.5a (durability bar, classes
 *     allowed, item set bonuses, sockets — only top-line stats today).
 *   – Drag ghost preview (uses native dataTransfer drag image only).
 *   – Mobile gesture: swipe-down-to-close not wired (relies on header X).
 */

import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type DragEvent as ReactDragEvent,
  type MouseEvent as ReactMouseEvent,
} from 'react';

import { SirusFrame } from './SirusFrame.js';
import { useEquip } from '../../state/equip-store.js';
import { useMatBag } from '../../state/mat-bag-store.js';
import { useUI } from '../../state/store.js';
import { ITEMS, RARITY_COLOUR, type ItemDef, type Rarity } from '../../data/equipment.js';

/** Drag-and-drop mime per spec — namespaced so other panels don't collide. */
const DND_MIME = 'application/aiwari-item';

/** Number of bags including the always-visible backpack. */
const BAG_COUNT = 5;

/** Slots per bag (WotLK 3.3.5a backpack capacity). */
const SLOTS_PER_BAG = 16;

/** 4×4 grid → derived for the CSS template. */
const GRID_COLS = 4;

/** Px offset between cascaded bag windows (matches Sirus default). */
const CASCADE_OFFSET_PX = 64;

/** Russian-locale rarity → tooltip subtitle. */
const RARITY_LABEL: Record<Rarity, string> = {
  common: 'обычный',
  uncommon: 'необычный',
  rare: 'редкий',
  epic: 'эпический',
  legendary: 'легендарный',
};

/** Russian-locale bag display names for the header chip. */
const BAG_NAMES: readonly string[] = [
  'Рюкзак',
  'Холщовая сумка',
  'Кожаная сумка',
  'Сумка волхва',
  'Кошель травника',
];

/** Per-bag tint for the bag-bar icon (last is the matbag purple reagent). */
const BAG_TINTS: readonly string[] = [
  'var(--aiwari-frame-stone)',
  'hsl(35 45% 30%)',
  'hsl(20 35% 28%)',
  'hsl(220 35% 30%)',
  'var(--aiwari-quality-epic)',
];

/** Single inventory cell: undefined = empty slot. */
interface InvCell {
  itemId: string;
  qty: number;
}

/** Drag payload — JSON-stringified into dataTransfer. */
interface DragPayload {
  bagIdx: number;
  slotIdx: number;
  itemId: string;
}

/** Right-click context-menu state. */
interface ContextMenu {
  x: number;
  y: number;
  bagIdx: number;
  slotIdx: number;
  itemId: string;
}

/** Tooltip hover state. */
interface TooltipState {
  x: number;
  y: number;
  item: ItemDef;
  qty: number;
}

/**
 * Build the empty grid for a bag (`SLOTS_PER_BAG` slots, all undefined).
 * Returns `(InvCell | undefined)[]` so React's reconciliation stays stable
 * if we later persist sparse arrays (vs a Record keyed by slotIdx).
 */
function emptyBag(): (InvCell | undefined)[] {
  return new Array<InvCell | undefined>(SLOTS_PER_BAG).fill(undefined);
}

/** Resolve a slot border-colour token from item rarity. */
function rarityVar(rarity: Rarity | undefined): string {
  switch (rarity) {
    case 'common':
      return 'var(--aiwari-quality-common)';
    case 'uncommon':
      return 'var(--aiwari-quality-uncommon)';
    case 'rare':
      return 'var(--aiwari-quality-rare)';
    case 'epic':
      return 'var(--aiwari-quality-epic)';
    case 'legendary':
      return 'var(--aiwari-quality-legendary)';
    default:
      return 'var(--aiwari-frame-edge)';
  }
}

/** Look up an ItemDef by id, falling back to undefined for unknown ids. */
function findItem(itemId: string): ItemDef | undefined {
  return ITEMS.find((i) => i.id === itemId);
}

/**
 * Convert the matbag store's `Record<id, qty>` map into `(InvCell|undefined)[]`
 * so the reagent bag (#4) renders with the same code path as the others.
 * Entries beyond `SLOTS_PER_BAG` are clipped — visually the player sees a
 * "сумка переполнена" banner once we wire that, for now they just drop off.
 */
function matBagToCells(bag: Record<string, number>): (InvCell | undefined)[] {
  const cells = emptyBag();
  let i = 0;
  for (const [id, qty] of Object.entries(bag)) {
    if (i >= SLOTS_PER_BAG) break;
    if (qty > 0) cells[i++] = { itemId: id, qty };
  }
  return cells;
}

/**
 * Sigil fallback rendered when an item has no real icon binding. Keeps the
 * bag visually populated while the real icon registry catches up.
 */
function ItemSigil({ item }: { item: ItemDef | undefined }) {
  const sigil = item?.sigil ?? '?';
  const tint = item?.tint ?? 'var(--aiwari-frame-stone)';
  return (
    <span
      aria-hidden
      style={{
        position: 'absolute',
        inset: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: `linear-gradient(135deg, ${tint}, var(--aiwari-frame-stone))`,
        fontFamily: 'var(--aiwari-font-display)',
        fontSize: 14,
        fontWeight: 700,
        color: 'var(--aiwari-fg)',
        textShadow: '0 1px 0 #000',
        borderRadius: 2,
      }}
    >
      {sigil}
    </span>
  );
}

/** Bag-bar bottom button (one per bag). 32 px desktop, 24 px touch. */
function BagBarButton({
  index,
  open,
  onToggle,
  onShiftClick,
  count,
}: {
  index: number;
  open: boolean;
  onToggle: () => void;
  onShiftClick: () => void;
  count: number;
}) {
  const onClick = (e: ReactMouseEvent<HTMLButtonElement>) => {
    if (e.shiftKey && index === 0) {
      onShiftClick();
      return;
    }
    onToggle();
  };
  return (
    <button
      type="button"
      onClick={onClick}
      data-aiwari-bag-bar-button
      aria-label={`${BAG_NAMES[index] ?? 'Сумка'} (${count})`}
      title={`${BAG_NAMES[index] ?? 'Сумка'} — ${count} / ${SLOTS_PER_BAG}`}
      className="aiwari-icon-slot aiwari-stone-btn"
      style={{
        width: 'var(--aiwari-slot-micro)',
        height: 'var(--aiwari-slot-micro)',
        background: BAG_TINTS[index] ?? 'var(--aiwari-frame-stone)',
        borderColor: open ? 'var(--aiwari-gold)' : 'var(--aiwari-frame-edge)',
        boxShadow: open ? '0 0 8px hsl(44 100% 49% / 0.55)' : undefined,
      }}
    >
      <span
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: 'var(--aiwari-font-display)',
          fontSize: 'var(--aiwari-text-sm)',
          fontWeight: 700,
          color: 'var(--aiwari-fg)',
          textShadow: '0 1px 0 #000',
          pointerEvents: 'none',
        }}
      >
        {index === 0 ? 'B' : `F${index}`}
      </span>
      {count > 0 && (
        <span
          style={{
            position: 'absolute',
            right: 2,
            bottom: 1,
            fontFamily: 'var(--aiwari-font-mono)',
            fontSize: 'var(--aiwari-text-xs)',
            color: 'var(--aiwari-gold-soft)',
            textShadow: '0 1px 0 #000',
            pointerEvents: 'none',
          }}
        >
          {count}
        </span>
      )}
    </button>
  );
}

/** Tooltip card shown on hover. Pure render of the placeholder/registry data. */
function ItemTooltipCard({ tip }: { tip: TooltipState }) {
  const { item, qty, x, y } = tip;
  const w = 240;
  const h = 200;
  const left = Math.min(window.innerWidth - w - 8, x + 14);
  const top = Math.min(window.innerHeight - h - 8, y + 8);
  const sellPrice = Math.max(
    1,
    Math.floor(
      item.ilvl * 5 * (1 + ['common', 'uncommon', 'rare', 'epic', 'legendary'].indexOf(item.rarity) * 0.5),
    ),
  );
  return (
    <div
      role="tooltip"
      style={{
        position: 'fixed',
        left,
        top,
        width: w,
        zIndex: 60,
        pointerEvents: 'none',
        fontFamily: 'var(--aiwari-font-body)',
      }}
    >
      <SirusFrame size="sm">
        <div
          style={{
            fontFamily: 'var(--aiwari-font-display)',
            fontSize: 14,
            fontWeight: 600,
            color: rarityVar(item.rarity),
            marginBottom: 2,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {item.name.ru}
          {qty > 1 ? (
            <span
              style={{
                marginLeft: 6,
                fontFamily: 'var(--aiwari-font-mono)',
                fontSize: 11,
                color: 'var(--aiwari-fg-muted)',
              }}
            >
              ×{qty}
            </span>
          ) : null}
        </div>
        <div
          style={{
            fontFamily: 'var(--aiwari-font-caps)',
            fontSize: 10,
            color: 'var(--aiwari-fg-muted)',
            marginBottom: 6,
            letterSpacing: 1,
            textTransform: 'uppercase',
          }}
        >
          {RARITY_LABEL[item.rarity]} · ур.предм. {item.ilvl}
          {item.reqLevel > 1 ? ` · треб. ур. ${item.reqLevel}` : ''}
        </div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '2px 8px',
            fontFamily: 'var(--aiwari-font-mono)',
            fontSize: 11,
            color: 'var(--aiwari-fg)',
          }}
        >
          {item.stats.hp ? <span>HP +{item.stats.hp}</span> : null}
          {item.stats.armor ? <span>Броня +{item.stats.armor}</span> : null}
          {item.stats.pow ? <span>Сила +{item.stats.pow}</span> : null}
          {item.stats.resource ? <span>Ресурс +{item.stats.resource}</span> : null}
          {item.stats.crit ? <span>Крит +{item.stats.crit}%</span> : null}
          {item.stats.haste ? <span>Скор. +{item.stats.haste}%</span> : null}
          {item.stats.mastery ? <span>Маст. +{item.stats.mastery}%</span> : null}
          {item.stats.vers ? <span>Универс. +{item.stats.vers}%</span> : null}
        </div>
        <div
          style={{
            marginTop: 8,
            paddingTop: 6,
            borderTop: '1px solid var(--aiwari-frame-edge)',
            fontFamily: 'var(--aiwari-font-mono)',
            fontSize: 10,
            color: 'var(--aiwari-fg-muted)',
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <span>Цена продажи</span>
          <span style={{ color: 'var(--aiwari-gold)' }}>{sellPrice} 💎</span>
        </div>
        <div
          style={{
            marginTop: 4,
            fontFamily: 'var(--aiwari-font-body)',
            fontSize: 10,
            color: 'var(--aiwari-fg-dim)',
            fontStyle: 'italic',
          }}
        >
          ПКМ — действия · Shift+ЛКМ — в чат
        </div>
      </SirusFrame>
    </div>
  );
}

/** Single bag window: title bar + 4×4 slot grid. Self-contained chrome. */
interface BagWindowProps {
  bagIdx: number;
  cells: (InvCell | undefined)[];
  selected: { bagIdx: number; slotIdx: number } | null;
  bottomOffsetPx: number;
  isMobile: boolean;
  onClose: () => void;
  onSelectSlot: (bagIdx: number, slotIdx: number) => void;
  onSlotMouseEnter: (bagIdx: number, slotIdx: number, e: ReactMouseEvent<HTMLButtonElement>) => void;
  onSlotMouseMove: (bagIdx: number, slotIdx: number, e: ReactMouseEvent<HTMLButtonElement>) => void;
  onSlotMouseLeave: () => void;
  onSlotContextMenu: (bagIdx: number, slotIdx: number, e: ReactMouseEvent<HTMLButtonElement>) => void;
  onSlotShiftClick: (bagIdx: number, slotIdx: number) => void;
  onSlotDragStart: (bagIdx: number, slotIdx: number, e: ReactDragEvent<HTMLButtonElement>) => void;
  onSlotDrop: (bagIdx: number, slotIdx: number, e: ReactDragEvent<HTMLButtonElement>) => void;
}

function BagWindow(props: BagWindowProps) {
  const {
    bagIdx,
    cells,
    selected,
    bottomOffsetPx,
    isMobile,
    onClose,
    onSelectSlot,
    onSlotMouseEnter,
    onSlotMouseMove,
    onSlotMouseLeave,
    onSlotContextMenu,
    onSlotShiftClick,
    onSlotDragStart,
    onSlotDrop,
  } = props;

  const wrapStyle: CSSProperties = isMobile
    ? {
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: 0,
        top: 'auto',
        height: '70vh',
        zIndex: 40 + bagIdx,
        animation: 'aiwari-bag-slide 180ms ease-out',
      }
    : {
        position: 'fixed',
        right: 16,
        bottom: 16 + bottomOffsetPx,
        width: 192,
        zIndex: 40 + bagIdx,
      };

  return (
    <div data-aiwari-bag-window data-bag-idx={bagIdx} style={wrapStyle}>
      <SirusFrame size="sm">
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 8,
          }}
        >
          <span
            style={{
              fontFamily: 'var(--aiwari-font-display)',
              fontSize: 12,
              fontWeight: 600,
              color: 'var(--aiwari-gold-soft)',
              letterSpacing: 0.5,
            }}
          >
            {BAG_NAMES[bagIdx] ?? 'Сумка'}
          </span>
          <button
            type="button"
            onClick={onClose}
            aria-label="Закрыть сумку"
            style={{
              background: 'transparent',
              border: 'none',
              color: 'var(--aiwari-fg-muted)',
              fontSize: 16,
              lineHeight: 1,
              cursor: 'pointer',
              padding: '0 2px',
            }}
          >
            ×
          </button>
        </div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${GRID_COLS}, var(--aiwari-slot))`,
            gridAutoRows: 'var(--aiwari-slot)',
            gap: 'var(--aiwari-gap)',
            justifyContent: 'center',
          }}
        >
          {cells.map((cell, slotIdx) => {
            const item = cell ? findItem(cell.itemId) : undefined;
            const isSelected =
              !!selected && selected.bagIdx === bagIdx && selected.slotIdx === slotIdx;
            const borderColour = isSelected
              ? 'var(--aiwari-gold)'
              : item
                ? rarityVar(item.rarity)
                : 'var(--aiwari-frame-edge)';
            return (
              <button
                key={slotIdx}
                type="button"
                className="aiwari-icon-slot aiwari-stone-btn"
                draggable={!!cell}
                onDragStart={(e) => cell && onSlotDragStart(bagIdx, slotIdx, e)}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.dataTransfer.dropEffect = 'move';
                }}
                onDrop={(e) => onSlotDrop(bagIdx, slotIdx, e)}
                onClick={(e) => {
                  if (e.shiftKey) {
                    if (cell) onSlotShiftClick(bagIdx, slotIdx);
                    return;
                  }
                  if (cell) onSelectSlot(bagIdx, slotIdx);
                }}
                onContextMenu={(e) => {
                  e.preventDefault();
                  if (cell) onSlotContextMenu(bagIdx, slotIdx, e);
                }}
                onMouseEnter={(e) => onSlotMouseEnter(bagIdx, slotIdx, e)}
                onMouseMove={(e) => onSlotMouseMove(bagIdx, slotIdx, e)}
                onMouseLeave={onSlotMouseLeave}
                style={{
                  borderColor: borderColour,
                  boxShadow: isSelected
                    ? '0 0 8px hsl(44 100% 49% / 0.6), inset 0 0 0 1px hsl(44 100% 49% / 0.4)'
                    : undefined,
                }}
              >
                {cell ? <ItemSigil item={item} /> : null}
                {cell && cell.qty > 1 ? (
                  <span
                    style={{
                      position: 'absolute',
                      right: 2,
                      bottom: 1,
                      fontFamily: 'var(--aiwari-font-mono)',
                      fontSize: 10,
                      color: 'var(--aiwari-fg)',
                      textShadow: '0 1px 0 #000',
                      pointerEvents: 'none',
                    }}
                  >
                    {cell.qty}
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>
      </SirusFrame>
    </div>
  );
}

export interface BagsProps {
  /** Optional override for the local-only bag state (Storybook / tests). */
  initialBags?: ((InvCell | undefined)[])[];
}

function BagsImpl({ initialBags }: BagsProps = {}) {
  const stage = useUI((s) => s.stage);
  const equipped = useEquip((s) => s.equipped);
  const matBag = useMatBag((s) => s.bag);

  // Local-only inventory state. The reagent bag (idx 4) is read straight
  // from useMatBag — see `bagsForRender` below.
  const [bags, setBags] = useState<((InvCell | undefined)[])[]>(() => {
    if (initialBags && initialBags.length === BAG_COUNT) return initialBags.map((b) => b.slice());
    // Seed bag 0 with whatever's in the equip-store but unequipped — gives
    // the player something to look at on first load. Pure visual scaffold.
    const seed = emptyBag();
    let i = 0;
    for (const itemId of Object.values(equipped)) {
      if (!itemId || i >= 6) continue;
      seed[i++] = { itemId, qty: 1 };
    }
    return [seed, emptyBag(), emptyBag(), emptyBag(), emptyBag()];
  });

  // The reagent bag is reactive to useMatBag — derived view.
  const bagsForRender = useMemo<((InvCell | undefined)[])[]>(() => {
    const out = bags.slice();
    out[4] = matBagToCells(matBag);
    return out;
  }, [bags, matBag]);

  const [openBags, setOpenBags] = useState<boolean[]>(() => [false, false, false, false, false]);
  const [selected, setSelected] = useState<{ bagIdx: number; slotIdx: number } | null>(null);
  const [tooltip, setTooltip] = useState<TooltipState | null>(null);
  const [ctxMenu, setCtxMenu] = useState<ContextMenu | null>(null);

  // Detect coarse pointer (touch) for the mobile sheet layout.
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(pointer: coarse)');
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);

  /** Toggle one bag (mobile: also closes any other open bag first). */
  const toggleBag = useCallback(
    (idx: number) => {
      setOpenBags((cur) => {
        const next = cur.slice();
        if (isMobile) {
          for (let i = 0; i < next.length; i += 1) {
            if (i !== idx) next[i] = false;
          }
        }
        next[idx] = !next[idx];
        return next;
      });
    },
    [isMobile],
  );

  /** Toggle every bag at once. If any bag is open, close them all. */
  const toggleAll = useCallback(() => {
    setOpenBags((cur) => {
      const anyOpen = cur.some(Boolean);
      return cur.map(() => !anyOpen);
    });
  }, []);

  // Keyboard: B / И → bag 0. Shift+B / Shift+И → all. F1..F4 → bags 1..4.
  useEffect(() => {
    if (stage !== 'world') return; // creator/realm-select shouldn't steal keys
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      if (e.ctrlKey || e.altKey || e.metaKey) return;
      const k = e.key;
      if (k === 'b' || k === 'B' || k === 'и' || k === 'И') {
        e.preventDefault();
        if (e.shiftKey) toggleAll();
        else toggleBag(0);
        return;
      }
      if (e.shiftKey) return;
      if (k === 'F1') {
        e.preventDefault();
        toggleBag(1);
        return;
      }
      if (k === 'F2') {
        e.preventDefault();
        toggleBag(2);
        return;
      }
      if (k === 'F3') {
        e.preventDefault();
        toggleBag(3);
        return;
      }
      if (k === 'F4') {
        e.preventDefault();
        toggleBag(4);
        return;
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [stage, toggleAll, toggleBag]);

  // Close ctx menu on outside click / Esc.
  useEffect(() => {
    if (!ctxMenu) return;
    const onDown = () => setCtxMenu(null);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setCtxMenu(null);
    };
    window.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [ctxMenu]);

  // -------- Slot interaction handlers ------------------------------------
  const dragSourceRef = useRef<DragPayload | null>(null);

  const handleSelectSlot = useCallback((bagIdx: number, slotIdx: number) => {
    setSelected({ bagIdx, slotIdx });
  }, []);

  const handleShiftClick = useCallback(
    (bagIdx: number, slotIdx: number) => {
      const cells = bagsForRender[bagIdx];
      if (!cells) return;
      const cell = cells[slotIdx];
      if (!cell) return;
      const item = findItem(cell.itemId);
      const detail = {
        itemId: cell.itemId,
        qty: cell.qty,
        name: item?.name.ru ?? cell.itemId,
        rarity: item?.rarity ?? 'common',
      };
      window.dispatchEvent(new CustomEvent('aiwari-chat-link', { detail }));
    },
    [bagsForRender],
  );

  const handleContextMenu = useCallback(
    (bagIdx: number, slotIdx: number, e: ReactMouseEvent<HTMLButtonElement>) => {
      const cells = bagsForRender[bagIdx];
      if (!cells) return;
      const cell = cells[slotIdx];
      if (!cell) return;
      setCtxMenu({
        x: e.clientX,
        y: e.clientY,
        bagIdx,
        slotIdx,
        itemId: cell.itemId,
      });
    },
    [bagsForRender],
  );

  const handleMouseEnter = useCallback(
    (bagIdx: number, slotIdx: number, e: ReactMouseEvent<HTMLButtonElement>) => {
      const cells = bagsForRender[bagIdx];
      if (!cells) return;
      const cell = cells[slotIdx];
      if (!cell) return;
      const item = findItem(cell.itemId);
      if (!item) return;
      setTooltip({ x: e.clientX, y: e.clientY, item, qty: cell.qty });
    },
    [bagsForRender],
  );

  const handleMouseMove = useCallback(
    (bagIdx: number, slotIdx: number, e: ReactMouseEvent<HTMLButtonElement>) => {
      const cells = bagsForRender[bagIdx];
      if (!cells) return;
      const cell = cells[slotIdx];
      if (!cell) return;
      const item = findItem(cell.itemId);
      if (!item) return;
      setTooltip({ x: e.clientX, y: e.clientY, item, qty: cell.qty });
    },
    [bagsForRender],
  );

  const handleMouseLeave = useCallback(() => {
    setTooltip(null);
  }, []);

  const handleDragStart = useCallback(
    (bagIdx: number, slotIdx: number, e: ReactDragEvent<HTMLButtonElement>) => {
      const cells = bagsForRender[bagIdx];
      if (!cells) return;
      const cell = cells[slotIdx];
      if (!cell) return;
      const payload: DragPayload = { bagIdx, slotIdx, itemId: cell.itemId };
      dragSourceRef.current = payload;
      try {
        e.dataTransfer.setData(DND_MIME, JSON.stringify(payload));
        e.dataTransfer.effectAllowed = 'move';
      } catch {
        // some browsers throw on custom mimes — ref fallback covers it.
      }
    },
    [bagsForRender],
  );

  const handleDrop = useCallback(
    (bagIdx: number, slotIdx: number, e: ReactDragEvent<HTMLButtonElement>) => {
      e.preventDefault();
      let payload: DragPayload | null = null;
      try {
        const raw = e.dataTransfer.getData(DND_MIME);
        if (raw) payload = JSON.parse(raw) as DragPayload;
      } catch {
        // ignore
      }
      if (!payload) payload = dragSourceRef.current;
      dragSourceRef.current = null;
      if (!payload) return;
      // Reagent bag (idx 4) is read-only from this UI — skip moves into/out of it.
      if (payload.bagIdx === 4 || bagIdx === 4) {
        console.log('[Bags] skip drag involving reagent bag', payload, { bagIdx, slotIdx });
        return;
      }
      if (payload.bagIdx === bagIdx && payload.slotIdx === slotIdx) return;
      setBags((cur) => {
        const next = cur.map((b) => b.slice());
        const fromBag = next[payload!.bagIdx];
        const toBag = next[bagIdx];
        if (!fromBag || !toBag) return cur;
        const fromCell = fromBag[payload!.slotIdx];
        const toCell = toBag[slotIdx];
        toBag[slotIdx] = fromCell;
        fromBag[payload!.slotIdx] = toCell;
        return next;
      });
    },
    [],
  );

  // -------- Bag-bar item counts (header chips) ---------------------------
  const counts = useMemo(
    () => bagsForRender.map((b) => b.filter(Boolean).length),
    [bagsForRender],
  );

  // -------- Context menu actions (stub) ----------------------------------
  const ctxActions = useMemo(() => {
    if (!ctxMenu) return null;
    const item = findItem(ctxMenu.itemId);
    const itemName = item?.name.ru ?? ctxMenu.itemId;
    return [
      {
        label: 'Использовать',
        danger: false,
        run: () => console.log('[Bags] use item', { ...ctxMenu, name: itemName }),
      },
      {
        label: 'Разделить стак',
        danger: false,
        run: () => console.log('[Bags] split stack', { ...ctxMenu, name: itemName }),
      },
      {
        label: 'Перетащить в чат',
        danger: false,
        run: () => {
          window.dispatchEvent(
            new CustomEvent('aiwari-chat-link', {
              detail: {
                itemId: ctxMenu.itemId,
                qty: 1,
                name: itemName,
                rarity: item?.rarity ?? 'common',
              },
            }),
          );
        },
      },
      {
        label: 'Уничтожить',
        danger: true,
        run: () => {
          console.log('[Bags] destroy item', { ...ctxMenu, name: itemName });
        },
      },
    ];
  }, [ctxMenu]);

  // -------- Render --------------------------------------------------------
  if (stage !== 'world') return null;

  // Stack open bags from bottom-up: backpack at bottom (offset 0), each
  // additional open bag 64 px above the previous *open* one.
  const offsets: number[] = [];
  let acc = 0;
  for (let i = 0; i < BAG_COUNT; i += 1) {
    offsets.push(acc);
    if (openBags[i]) acc += CASCADE_OFFSET_PX;
  }

  return (
    <>
      <style>{`
        @keyframes aiwari-bag-slide {
          from { transform: translateY(100%); opacity: 0.6; }
          to   { transform: translateY(0);    opacity: 1; }
        }
        @media (pointer: coarse) {
          [data-aiwari-bag-bar] { gap: 3px !important; }
          [data-aiwari-bag-bar-button] { width: 24px !important; height: 24px !important; }
        }
      `}</style>

      {/* Bag windows */}
      {bagsForRender.map((cells, bagIdx) =>
        openBags[bagIdx] ? (
          <BagWindow
            key={bagIdx}
            bagIdx={bagIdx}
            cells={cells}
            selected={selected}
            bottomOffsetPx={offsets[bagIdx] ?? 0}
            isMobile={isMobile}
            onClose={() => toggleBag(bagIdx)}
            onSelectSlot={handleSelectSlot}
            onSlotMouseEnter={handleMouseEnter}
            onSlotMouseMove={handleMouseMove}
            onSlotMouseLeave={handleMouseLeave}
            onSlotContextMenu={handleContextMenu}
            onSlotShiftClick={handleShiftClick}
            onSlotDragStart={handleDragStart}
            onSlotDrop={handleDrop}
          />
        ) : null,
      )}

      {/* Bag bar — bottom-right strip of 5 bag-icon buttons. */}
      <div
        data-aiwari-bag-bar
        style={{
          position: 'fixed',
          right: 16,
          bottom: 80,
          display: 'flex',
          flexDirection: 'row',
          gap: 'var(--aiwari-gap)',
          zIndex: 35,
        }}
      >
        {Array.from({ length: BAG_COUNT }).map((_, i) => (
          <BagBarButton
            key={i}
            index={i}
            open={openBags[i] ?? false}
            count={counts[i] ?? 0}
            onToggle={() => toggleBag(i)}
            onShiftClick={toggleAll}
          />
        ))}
      </div>

      {/* Tooltip */}
      {tooltip ? <ItemTooltipCard tip={tooltip} /> : null}

      {/* Context menu */}
      {ctxMenu && ctxActions ? (
        <div
          role="menu"
          onMouseDown={(e) => e.stopPropagation()}
          onContextMenu={(e) => e.preventDefault()}
          style={{
            position: 'fixed',
            left: Math.min(window.innerWidth - 200, ctxMenu.x),
            top: Math.min(window.innerHeight - 180, ctxMenu.y),
            zIndex: 70,
            minWidth: 180,
          }}
        >
          <SirusFrame size="sm">
            <div
              style={{
                fontFamily: 'var(--aiwari-font-caps)',
                fontSize: 10,
                color: 'var(--aiwari-gold-soft)',
                letterSpacing: 1,
                textTransform: 'uppercase',
                padding: '0 4px 6px',
                borderBottom: '1px solid var(--aiwari-frame-edge)',
                marginBottom: 4,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {findItem(ctxMenu.itemId)?.name.ru ?? ctxMenu.itemId}
            </div>
            {ctxActions.map((action) => (
              <button
                key={action.label}
                type="button"
                onClick={() => {
                  action.run();
                  setCtxMenu(null);
                }}
                style={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'left',
                  background: 'transparent',
                  border: 'none',
                  padding: '6px 8px',
                  fontFamily: 'var(--aiwari-font-body)',
                  fontSize: 12,
                  color: action.danger ? 'hsl(0 70% 60%)' : 'var(--aiwari-fg)',
                  cursor: 'pointer',
                  borderRadius: 2,
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.background =
                    'hsl(44 100% 49% / 0.15)';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
                }}
              >
                {action.label}
              </button>
            ))}
          </SirusFrame>
        </div>
      ) : null}
    </>
  );
}

/**
 * R232q86zdb (Wave 2 #1): React.memo wrapper.
 *
 * Bags does NOT subscribe to net-store.players — but `useEquip` /
 * `useMatBag` patch on every loot pickup, and the parent SirusHud
 * re-renders on every UI stage change. Memo guards against pure parent
 * re-renders (same `initialBags` prop reference).
 */
export const Bags = memo(
  BagsImpl,
  (a, b) => a.initialBags === b.initialBags,
);

/* RARITY_COLOUR is intentionally re-exported here for callers that want to
 * derive their own colour from the same palette without re-importing the
 * equipment module. Kept stable across UI rebuild phases. */
export { RARITY_COLOUR };
